import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Rocket, Loader2, CheckCircle2, AlertTriangle, Image as ImageIcon,
  FileText, BookOpen, BarChart3, RefreshCw, X,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const STAGE_META = {
  text:        { label: 'Sections',    icon: FileText,  color: '#004ac6' },
  images:      { label: 'Images',      icon: ImageIcon, color: '#0891b2' },
  infographic: { label: 'Infographic', icon: BarChart3, color: '#7c3aed' },
  references:  { label: 'References',  icon: BookOpen,  color: '#16a34a' },
  done:        { label: 'Complete',    icon: CheckCircle2, color: '#16a34a' },
};

/**
 * Drives the strict 4-stage sequential pipeline:
 *   Stage 1 — text for every tier-allowed section, one at a time
 *   Stage 2 — image for every section, one at a time
 *   Stage 3 — infographic
 *   Stage 4 — references
 *
 * Polls /full-blueprint/status every 4s while running, then auto-stops.
 */
export default function FullBlueprintPipelinePanel({ countryId, onComplete }) {
  const [status, setStatus] = useState(null);
  const [starting, setStarting] = useState(false);
  const [forceRegen, setForceRegen] = useState(false);
  const pollRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    if (!countryId) return null;
    try {
      const { data } = await axios.get(`${API}/countries/${countryId}/full-blueprint/status`, { headers: auth() });
      setStatus(data);
      return data;
    } catch (e) {
      // 401/403 just means we shouldn't be polling; bail silently.
      return null;
    }
  }, [countryId]);

  // Initial load + clean polling lifecycle.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      const data = await fetchStatus();
      if (cancelled) return;
      if (data?.running) {
        pollRef.current = setTimeout(tick, 4000);
      } else if (data?.stage === 'done' && pollRef.current) {
        // pipeline just finished — let parent refresh once.
        if (onComplete) onComplete();
        pollRef.current = null;
      }
    };
    tick();
    return () => {
      cancelled = true;
      if (pollRef.current) clearTimeout(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryId]);

  const handleStart = async () => {
    setStarting(true);
    try {
      const { data } = await axios.post(
        `${API}/countries/${countryId}/generate-full-blueprint`,
        { force_regenerate: forceRegen },
        { headers: auth() },
      );
      if (data.already_running) {
        toast.info('A pipeline is already running for this country.');
      } else {
        toast.success(data.detail || 'Pipeline started');
      }
      // Kick the poll loop immediately
      const data2 = await fetchStatus();
      if (data2?.running && !pollRef.current) {
        const tick = async () => {
          const d = await fetchStatus();
          if (d?.running) pollRef.current = setTimeout(tick, 4000);
          else { if (onComplete) onComplete(); pollRef.current = null; }
        };
        pollRef.current = setTimeout(tick, 4000);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to start pipeline');
    } finally {
      setStarting(false);
    }
  };

  const running = !!status?.running;
  const total = status?.total_steps || 0;
  const done = status?.steps_done || 0;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const stage = status?.stage || null;
  const stageMeta = stage ? STAGE_META[stage] : null;
  const tierKeys = status?.tier_keys || [];
  const textDone = new Set(status?.text_done || []);
  const imagesDone = new Set(status?.images_done || []);
  const errors = status?.errors || [];

  return (
    <div className="p-5 bg-white rounded-xl shadow-sm border border-[#c3c6d7]/40" data-testid="full-blueprint-pipeline-panel">
      <div className="flex items-center gap-2 mb-3">
        <Rocket className="w-4 h-4 text-[#7c3aed]" />
        <span className="text-xs font-bold uppercase tracking-[0.1em] text-[#434655]">Full Blueprint Pipeline</span>
        {running && (
          <span className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-blue-50 text-blue-700">
            <Loader2 className="w-3 h-3 animate-spin" /> Running
          </span>
        )}
        {!running && status?.stage === 'done' && (
          <span className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-emerald-50 text-emerald-700">
            <CheckCircle2 className="w-3 h-3" /> Done
          </span>
        )}
      </div>

      {/* Idle state — show launch button */}
      {!running && (
        <div className="space-y-3" data-testid="pipeline-idle">
          <p className="text-[11px] text-[#737686] leading-relaxed">
            Strict sequential build: <strong>Sections → Images → Infographic → References</strong>. Runs one item at a time, in order.
          </p>
          <label className="flex items-start gap-2 text-[11px] text-[#434655] cursor-pointer select-none">
            <input
              type="checkbox"
              checked={forceRegen}
              onChange={(e) => setForceRegen(e.target.checked)}
              className="mt-0.5 accent-[#7c3aed]"
              data-testid="pipeline-force-checkbox"
            />
            <span>Regenerate completed sections too (otherwise they're skipped)</span>
          </label>
          <button
            type="button"
            onClick={handleStart}
            disabled={starting}
            className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-[#7c3aed] hover:bg-[#6d28d9] disabled:opacity-50 text-white text-xs font-bold uppercase tracking-widest transition"
            data-testid="pipeline-start-btn"
          >
            {starting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Rocket className="w-3.5 h-3.5" />}
            {status?.stage === 'done' ? 'Run pipeline again' : 'Generate Full Blueprint'}
          </button>
          {status?.stage === 'done' && (
            <div className="text-[10px] text-[#737686]">
              Last run: {status.text_done?.length || 0} sections · {status.images_done?.length || 0} images
              {status.infographic_done && ' · infographic'}{status.references_done && ' · references'}
            </div>
          )}
        </div>
      )}

      {/* Running / completed state */}
      {(running || status?.stage === 'done') && (
        <div className="space-y-3" data-testid="pipeline-progress">
          {/* Current step */}
          <div className="p-3 rounded-lg bg-gradient-to-br from-[#f8f9ff] to-white border border-[#eff4ff]">
            <div className="flex items-center gap-2 mb-1.5">
              {stageMeta && <stageMeta.icon className="w-3.5 h-3.5" style={{ color: stageMeta.color }} />}
              <span className="text-[10px] font-black uppercase tracking-widest" style={{ color: stageMeta?.color || '#737686' }}>
                Stage: {stageMeta?.label || '—'}
              </span>
            </div>
            <div className="text-xs font-bold text-[#0b1c30] truncate" title={status?.current_label || ''}>
              {status?.current_label || '—'}
            </div>
          </div>

          {/* Progress bar */}
          <div>
            <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-widest text-[#737686] mb-1.5">
              <span>{done} of {total} steps</span>
              <span>{pct}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-[#eff4ff] overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#004ac6] via-[#0891b2] to-[#7c3aed] transition-all duration-500 ease-out"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>

          {/* Stage breakdown */}
          <div className="grid grid-cols-2 gap-2 pt-1">
            <StageStat
              icon={FileText}
              color="#004ac6"
              label="Text"
              done={status?.text_done?.length || 0}
              total={tierKeys.length}
              isCurrent={stage === 'text'}
            />
            <StageStat
              icon={ImageIcon}
              color="#0891b2"
              label="Images"
              done={status?.images_done?.length || 0}
              total={tierKeys.length}
              isCurrent={stage === 'images'}
            />
            <StageStat
              icon={BarChart3}
              color="#7c3aed"
              label="Infographic"
              done={status?.infographic_done ? 1 : 0}
              total={1}
              isCurrent={stage === 'infographic'}
            />
            <StageStat
              icon={BookOpen}
              color="#16a34a"
              label="References"
              done={status?.references_done ? 1 : 0}
              total={1}
              isCurrent={stage === 'references'}
            />
          </div>

          {/* Per-section dots — visual heatmap of the build */}
          {tierKeys.length > 0 && (
            <div data-testid="pipeline-dots">
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#737686] mb-1.5">Sections</div>
              <div className="flex flex-wrap gap-1">
                {tierKeys.map((k) => {
                  const t = textDone.has(k);
                  const i = imagesDone.has(k);
                  let bg = '#e5e7eb';   // not started
                  if (t && i) bg = '#7c3aed';
                  else if (t) bg = '#004ac6';
                  return (
                    <span
                      key={k}
                      title={`${k}: text=${t ? '✓' : '–'} image=${i ? '✓' : '–'}`}
                      className="w-2 h-2 rounded-sm"
                      style={{ background: bg }}
                    />
                  );
                })}
              </div>
              <div className="flex items-center gap-3 text-[9px] font-bold uppercase tracking-widest text-[#737686] mt-2">
                <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[#e5e7eb]" /> pending</span>
                <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[#004ac6]" /> text</span>
                <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[#7c3aed]" /> text+image</span>
              </div>
            </div>
          )}

          {/* Errors */}
          {errors.length > 0 && (
            <div className="p-2.5 rounded-lg bg-amber-50 border border-amber-200 text-[10px] font-mono text-amber-900 max-h-20 overflow-y-auto" data-testid="pipeline-errors">
              <div className="flex items-center gap-1 font-bold mb-1 uppercase tracking-widest">
                <AlertTriangle className="w-3 h-3" /> Skipped {errors.length} step{errors.length > 1 ? 's' : ''}
              </div>
              {errors.slice(-5).map((e, i) => (
                <div key={i} className="truncate">{e}</div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StageStat({ icon: Icon, color, label, done, total, isCurrent }) {
  const ok = done >= total && total > 0;
  return (
    <div
      className={`p-2 rounded-lg border ${isCurrent ? 'border-[#7c3aed]/40 bg-[#f5f3ff]' : 'border-[#eff4ff] bg-white'}`}
    >
      <div className="flex items-center gap-1.5 mb-0.5">
        <Icon className="w-3 h-3" style={{ color }} />
        <span className="text-[9px] font-black uppercase tracking-widest text-[#737686]">{label}</span>
        {isCurrent && <Loader2 className="w-2.5 h-2.5 animate-spin ml-auto" style={{ color }} />}
        {!isCurrent && ok && <CheckCircle2 className="w-2.5 h-2.5 ml-auto" style={{ color: '#16a34a' }} />}
      </div>
      <div className="text-[11px] font-bold text-[#0b1c30]">{done}/{total}</div>
    </div>
  );
}
